import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Crown, Rocket } from "lucide-react";
import { BRAND, HERO_COPY, HERO_FEATURES, HERO_TAGS, LINKS, TECH_STACK } from "@/config/kingbullish";
import { FeatureCard } from "@/components/FeatureCard";
import { UnlockTicker } from "@/components/UnlockTicker";
import { SocialLinks } from "@/components/SocialLinks";
import { MaskedLine, FadeIn } from "@/components/motion/Reveal";
import { SolanaLogo } from "@/components/brand/SolanaLogo";
import { ArtworkImage } from "@/components/ArtworkImage";
import { ARTWORK, ARTWORK_SIZES } from "@/config/artwork";
import { scrollToId } from "@/lib/lenis";

const TechPanel = ({ className = "" }) => (
  <FadeIn delay={1.3} className={className}>
    <div className="glass glass-neon hud h-full rounded-lg p-3" style={{ "--hud-color": "#A855F7" }} data-testid="hero-tech-panel">
      <ul className="flex flex-col gap-2.5">
        {TECH_STACK.map(({ label, icon: Icon }) => (
          <li key={label} className="flex items-center gap-2.5">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded border border-vio/50 bg-vio/10 text-vio">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="font-display text-[0.6rem] font-semibold tracking-[0.14em] text-white/90">{label}</span>
          </li>
        ))}
      </ul>
    </div>
  </FadeIn>
);

const SolanaPanel = ({ className = "" }) => (
  <FadeIn delay={1.45} className={className}>
    <div className="glass hud h-full rounded-lg p-3" style={{ "--hud-color": "#14F195" }} data-testid="hero-powered-by-solana-panel">
      <div className="flex items-center gap-2.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-gradient-to-br from-[#9945FF]/35 to-[#14F195]/35 ring-1 ring-white/15">
          <SolanaLogo size={18} />
        </span>
        <div className="leading-none">
          <div className="font-body text-[0.58rem] font-semibold tracking-[0.22em] text-white/60">POWERED BY</div>
          <div className="mt-0.5 font-display text-[0.8rem] font-bold tracking-wider text-white">SOLANA</div>
        </div>
      </div>
      <div className="mt-2.5 border-t border-white/10 pt-2 font-display text-[0.62rem] font-bold leading-relaxed tracking-[0.16em] text-neon">
        FASTER.<br />CHEAPER.<br />BIGGER.
      </div>
    </div>
  </FadeIn>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 50, damping: 18 });
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 50, damping: 18 });
  const shiftX = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 40, damping: 20 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bullY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const onMove = (e) => {
    if (reduceMotion) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative -mt-2 overflow-hidden pt-8 lg:pt-12"
      data-testid="hero-section"
    >
      <div className="hero-overlay" />

      <div className="container-x relative z-10 grid pb-16 lg:grid-cols-[1.05fr_1fr] lg:gap-6 lg:pb-24">
        {/* LEFT — `contents` on mobile places the emblem between the title and copy. */}
        <div className="contents lg:block lg:pt-8">
          <FadeIn delay={0.5} className="order-[-4] lg:order-none">
            <div className="tag-list" data-testid="hero-tags">
              {HERO_TAGS.map((t) => <span key={t}>{t}</span>)}
            </div>
          </FadeIn>

          <h1 className="order-[-3] mt-7 font-display font-black leading-[0.92] tracking-tight lg:order-none" data-testid="hero-title">
            <span className="relative block">
              <motion.span
                initial={{ opacity: 0, y: 10, scale: 0.7 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.25, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -top-7 left-0 text-gold glow-gold sm:-top-8"
              >
                <Crown className="h-7 w-7 sm:h-8 sm:w-8" fill="currentColor" />
              </motion.span>
              <MaskedLine delay={0.55} className="hero-h1" innerClassName="italic">
                <span className="title-king">KING</span><span className="title-bullish">BULLISH</span>
              </MaskedLine>
            </span>
            <MaskedLine delay={0.7} className="hero-h2 mt-3" innerClassName="italic text-neon glow-green">
              {BRAND.tagline}
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.85} className="order-[-2] mt-4 font-display text-base font-semibold tracking-[0.14em] text-white/90 md:text-lg lg:order-none" innerClassName="not-italic">
            A NEXT-GENERATION ECOSYSTEM
          </MaskedLine>

          <FadeIn delay={1}>
            <p className="mt-5 max-w-[520px] font-body text-base font-medium leading-relaxed text-white/75 sm:text-lg" data-testid="hero-copy">
              {HERO_COPY}
            </p>
          </FadeIn>

          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4 sm:gap-x-5" data-testid="hero-features">
            {HERO_FEATURES.map((f, i) => <FeatureCard key={f.id} feature={f} index={i} />)}
          </div>

          <FadeIn delay={1.55} className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
            <a href={LINKS.buy} className="btn-neon" data-testid="hero-buy-kbull-cta">
              BUY $KBULL <Rocket className="h-4 w-4" />
            </a>
            <button type="button" onClick={() => scrollToId("roadmap")} className="btn-outline group" data-testid="hero-view-roadmap-cta">
              VIEW ROADMAP <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </FadeIn>

          <div className="mt-5" data-testid="hero-community-area">
            <p className="mb-2.5 font-body text-xs font-semibold text-gold/85" data-testid="hero-community-heading">
              JOIN THE KINGBULLISH COMMUNITY
            </p>
            <SocialLinks placement="hero" compact />
          </div>

        </div>

        {/* RIGHT */}
        <div className="contents lg:relative lg:block lg:min-h-[700px] xl:min-h-[760px]" data-testid="hero-visual">
          <motion.div
            className="hero-art-stage"
            style={{ y: reduceMotion ? 0 : bullY, opacity: reduceMotion ? 1 : fade }}
            data-testid="hero-emblem-stage"
          >
            <motion.div
              className="hero-emblem-tilt"
              style={{ rotateX: reduceMotion ? 0 : rotateX, rotateY: reduceMotion ? 0 : rotateY, x: reduceMotion ? 0 : shiftX }}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.92, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: reduceMotion ? 0 : 1.4, delay: reduceMotion ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-emblem-tilt"
            >
              <div className="hero-emblem-float" data-testid="hero-emblem-float">
                <ArtworkImage
                  artwork={ARTWORK.hero}
                  sizes={ARTWORK_SIZES.hero}
                  priority
                  className="hero-emblem-img"
                  testId="hero-bull-image"
                />
              </div>
            </motion.div>
          </motion.div>

          <div className="mt-8 grid grid-cols-2 gap-3 lg:contents">
            <FadeIn delay={1.1} className="col-span-2 text-left lg:absolute lg:right-0 lg:top-2 lg:text-right" data-testid="hero-supporting-phrase">
              <div className="font-display text-[1.05rem] font-bold leading-[1.05] tracking-wide text-white sm:text-[1.25rem]">
                SAME<br />VISION.
                <span className="block text-neon glow-green">BIGGER<br />HORIZONS.</span>
              </div>
            </FadeIn>
            <TechPanel className="lg:absolute lg:right-0 lg:top-[36%] lg:w-[190px]" />
            <SolanaPanel className="lg:absolute lg:bottom-4 lg:right-0 lg:w-[190px]" />
          </div>
        </div>
        <UnlockTicker className="mt-8 lg:col-span-2" />
      </div>
    </section>
  );
};
